import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { Skeleton } from '@onbeef/components/skeleton';
import Tooltip from '@onbeef/components/tooltip';
import { LoyaltyRewardType } from '@onbeefapp/constants';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useSlug } from '../../hooks/useSlug';
import { useMerchantCache } from '../../queries/home';
import { useCartStore } from '../../stores/cart';
import { useAuthStore } from '../../stores/login';
import { useMerchantStore } from '../../stores/merchant';
import { useCartLoyaltyPoints } from './hooks/useCartLoyaltyPoints';

export const LoyaltyPointsCTA = () => {
  const navigate = useNavigate();
  const params = useSlug();

  const { isLoading } = useMerchantCache(params.slug);

  const { loyalty_programme, merchant } = useMerchantStore((state) => ({
    loyalty_programme: state.loyalty_programme,
    merchant: state.merchant,
  }));

  const user = useAuthStore((state) => state.user);
  const selectedReward = useCartStore((state) => state.selectedPointReward);

  const handleOpenLoyaltyDetail = () => navigate('/loyalty/detail');
  const handleOpenLoyaltyRewards = () => navigate('/loyalty/rewards');

  const userLoyalty = user?.loyalty_programmes?.find(
    (loyalty) => loyalty.merchant_id === merchant?.id,
  );
  const userPoints = userLoyalty?.reward?.customer_points;
  const availablePoints = userPoints?.available || 0;

  const isLoyaltyEnabled =
    loyalty_programme != null && loyalty_programme.status === 'ACTIVE';

  return (
    isLoyaltyEnabled &&
    availablePoints > 0 && (
      <Skeleton isLoading={isLoading} className="w-full h-32 rounded-lg">
        <div className="w-full px-4 pb-6">
          <div className="relative gradient-primary rounded-xl overflow-hidden shadow-lg">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>

            <div className="pt-4 px-6 flex items-center justify-between relative z-10">
              <div className="flex items-center">
                <span className="text-white text-lg font-bold">
                  {userLoyalty?.name || 'Fidelidade'}
                </span>
                <Tooltip text="Seu saldo atual de pontos pode ser trocado por recompensas exclusivas. Acumule mais pontos realizando novas compras.">
                  <div className="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs text-white cursor-pointer">
                    ?
                  </div>
                </Tooltip>
              </div>
              <button
                type="button"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-2"
                onClick={handleOpenLoyaltyDetail}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-white">
                      {availablePoints}
                    </span>
                    <span className="ml-1 text-white/80 font-medium">pts</span>
                  </div>
                </div>
              </button>
            </div>

            <div className="relative py-4">
              <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 h-8 w-5 bg-gray-50 rounded-r-full pointer-events-none"></div>
              <div className="absolute -right-2.5 top-1/2 -translate-y-1/2 h-8 w-5 bg-gray-50 rounded-l-full pointer-events-none"></div>
              <div className="border-t-2 border-dashed border-white/30 mx-4"></div>
            </div>

            <div className="px-4 pb-4 relative z-10">
              <button
                type="button"
                onClick={handleOpenLoyaltyRewards}
                className="bg-white text-primary py-3 rounded-lg text-sm font-semibold shadow-sm hover:bg-white/90 transition-colors flex items-center justify-center w-full cursor-pointer"
              >
                {selectedReward
                  ? 'Resgatando: ' + selectedReward.name
                  : 'Trocar meus pontos'}
              </button>
            </div>
          </div>
        </div>
      </Skeleton>
    )
  );
};

export const LoyaltyPointsRewardCartCTA = () => {
  const navigate = useNavigate();
  const params = useSlug();

  const { isLoading } = useMerchantCache(params.slug);

  const { loyalty_programme, merchant } = useMerchantStore((state) => ({
    loyalty_programme: state.loyalty_programme,
    merchant: state.merchant,
  }));

  const user = useAuthStore((state) => state.user);

  const { handleSelectReward, selectedPointReward } = useCartLoyaltyPoints();

  const handleOpenLoyaltyDetail = () => navigate('/loyalty/detail');

  const capitalize = (str: string): string =>
    str?.[0]?.toUpperCase() + str.slice(1) || '';

  const userLoyalty = user?.loyalty_programmes?.find(
    (loyalty) => loyalty.merchant_id === merchant?.id,
  );
  const userPoints = userLoyalty?.reward?.customer_points;
  const availablePoints = userPoints?.available || 0;

  const pointRewards = userLoyalty?.reward.point_rewards || [];

  const rewardAvailable = pointRewards.some(
    (pr) => availablePoints >= pr.quantity_points,
  );

  const isLoyaltyEnabled =
    loyalty_programme != null && loyalty_programme.status === 'ACTIVE';

  return (
    isLoyaltyEnabled &&
    !selectedPointReward &&
    rewardAvailable && (
      <Skeleton isLoading={isLoading} className="w-full h-10 rounded-lg">
        <div className="w-full mt-3 flex flex-col items-center gap-2">
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center">
              <StarIcon className="w-5 h-5" />
              <span className="ml-2 mr-3 font-bold text-sm">
                {capitalize(loyalty_programme?.name || '')}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                Seu saldo
              </span>
              <button
                type="button"
                onClick={handleOpenLoyaltyDetail}
                className="gradient-primary text-contrastText font-semibold text-sm rounded-lg px-3 py-1"
              >
                {availablePoints} pts
              </button>
            </div>
          </div>
          <div
            className="w-full flex items-center gap-2 overflow-auto"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cccccc transparent',
            }}
          >
            {pointRewards.map((reward) => {
              const isAvailable = availablePoints >= reward.quantity_points;

              return (
                <div
                  key={reward.id}
                  className="flex items-center justify-between w-4/5 md:w-2/5 min-w-4/5 h-20 flex-shrink-0 bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-100"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800 text-sm">
                      {reward.name}
                    </span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-1.5 w-16 bg-gray-300 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{
                            width: `${Math.min(
                              100,
                              (availablePoints / reward.quantity_points) * 100,
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-primary font-semibold">
                        {reward.quantity_points} pts
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelectReward(reward)}
                    disabled={!isAvailable}
                    className={cx(
                      'border rounded-lg px-4 py-1',
                      isAvailable
                        ? 'border-current text-primary font-medium text-sm hover:underline transition-all'
                        : 'border-gray-400 text-gray-400 text-sm',
                    )}
                  >
                    Resgatar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Skeleton>
    )
  );
};

export const LoyaltyPointsRegisterCTA = () => {
  const params = useSlug();
  const navigate = useNavigate();

  const { isLoading } = useMerchantCache(params.slug);

  const { loyalty_programme, merchant } = useMerchantStore((state) => ({
    loyalty_programme: state.loyalty_programme,
    merchant: state.merchant,
  }));

  const user = useAuthStore((state) => state.user);
  const userLoyalty = user?.loyalty_programmes?.find(
    (loyalty) => loyalty.merchant_id === merchant?.id,
  );

  const { getCurrentOrderPoints } = useCartLoyaltyPoints();

  const handleOpenLoyalty = () => {
    if (userLoyalty) {
      navigate('/loyalty/detail');
    } else {
      navigate('/loyalty', { state: { goBack: '/checkout' } });
    }
  };

  const capitalize = (str: string): string =>
    str?.[0]?.toUpperCase() + str.slice(1) || '';

  const isLoyaltyPointsEnabled =
    loyalty_programme != null &&
    loyalty_programme.status === 'ACTIVE' &&
    loyalty_programme.reward?.type === LoyaltyRewardType.POINTS;

  const currentOrderPoints = getCurrentOrderPoints();

  return (
    isLoyaltyPointsEnabled &&
    currentOrderPoints > 0 && (
      <Skeleton isLoading={isLoading} className="w-full h-auto rounded-lg mb-4">
        <div
          className="w-full p-4 mt-4 mb-2 rounded-lg bg-white shadow-sm border border-gray-100 flex items-center justify-between gap-3 cursor-pointer"
          onClick={handleOpenLoyalty}
        >
          {userLoyalty ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                  <StarIcon className="w-6 h-6 text-contrastText" />
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Você vai acumular {currentOrderPoints} pontos nesse pedido
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                  <StarIcon className="w-6 h-6 text-contrastText" />
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Ganhe{' '}
                  <span className="text-primary font-bold">
                    {currentOrderPoints} pontos
                  </span>{' '}
                  com esse pedido ao se inscrever no programa{' '}
                  <span className="text-primary font-bold">
                    {capitalize(loyalty_programme?.name || '')}
                  </span>
                  !
                </span>
              </div>
              <ChevronRightIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
            </>
          )}
        </div>
      </Skeleton>
    )
  );
};
